// The console's information architecture — one array, three renderings.
//
// The sidebar, the dashboard tile grid and the section landing pages are all
// built from `NAV_SECTIONS`, so a destination cannot exist in one and be
// missing from another. Adding a route here is the only step needed to make it
// reachable from every surface.
//
// ─── Why this replaced the previous four-group array ──────────────────────
//
// The old config listed eighteen destinations flat, grouped as "Treasury CRM /
// Product Control Plane / Account / Advanced Ops / Legacy", with five separate
// LEGACY rows sitting in the primary navigation. `Layout.test.tsx` pinned that
// shape, and its first assertion — Treasury CRM must be the FIRST group —
// recorded a product ruling that the Control Plane section was explicitly not
// allowed to overturn "by arriving".
//
// The operator has now overturned it directly. The console's organising
// principle is no longer "which subsystem does this belong to" but "what is
// the operator trying to do": eight sections, most collapsed, with every
// legacy tool behind a single LEGACY entry. `Layout.test.tsx` was rewritten in
// the same commit to pin the NEW invariants, which are the ones that still
// carry meaning: legacy routes stay out of primary navigation, keep their
// renamed labels, and stay flagged.
//
// ─── Shape ────────────────────────────────────────────────────────────────
//
// A section either IS a destination (`to` set, no `items`) or CONTAINS them
// (`items` set, no `to`). Nothing is both: a heading that is also a link is
// the control that everyone clicks by accident.

import type { ComponentType } from "react";
import {
  IconApprovals,
  IconCredits,
  IconDashboard,
  IconDevices,
  IconHealth,
  IconLegacy,
  IconOperations,
  IconPartners,
  IconPayables,
  IconProducts,
  IconRefunds,
  IconSession,
  IconSystem,
  IconTreasury,
  IconLayers,
  type IconProps,
} from "../ui/icons";

/** Marks a destination that is kept for reference rather than daily use. */
export type NavFlag = "legacy" | "deprecated" | "read-only";

export interface NavItem {
  to: string;
  label: string;
  icon?: ComponentType<IconProps>;
  /** ONE short line. Rendered on dashboard tiles and section landing cards.
   *  Kept to a handful of words on purpose — a tile that needs a sentence is
   *  a tile whose label is wrong. */
  blurb?: string;
  /** Legacy / Advanced Ops item — rendered visually secondary. */
  legacy?: boolean;
  flags?: ReadonlyArray<NavFlag>;
}

export interface NavSection {
  heading: string;
  icon?: ComponentType<IconProps>;
  /** Set when the section is itself a destination (renders as one link). */
  to?: string;
  blurb?: string;
  /** Set when the section contains destinations (renders as a group). */
  items?: ReadonlyArray<NavItem>;
  /** Sidebar groups start closed unless a child route is active. */
  defaultOpen?: boolean;
  /** Rendered visually secondary in the sidebar. */
  legacy?: boolean;
}

export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  {
    heading: "Dashboard",
    to: "/dashboard",
    icon: IconDashboard,
    blurb: "Console home",
  },
  {
    // /control-plane IS the product registry — there is no second "control
    // plane home" route to point a separate entry at, so Products is the
    // section's one destination and the draft → validate → review → publish
    // workflow hangs off a product rather than off the nav.
    heading: "Control Plane",
    icon: IconLayers,
    defaultOpen: true,
    items: [
      {
        to: "/control-plane",
        label: "Products",
        icon: IconProducts,
        blurb: "Manage Gao products",
      },
    ],
  },
  {
    heading: "Approvals",
    to: "/approvals",
    icon: IconApprovals,
    blurb: "Review pending actions",
  },
  {
    heading: "Treasury",
    to: "/treasury",
    icon: IconTreasury,
    blurb: "Funds and accounting",
  },
  {
    heading: "Operations",
    icon: IconOperations,
    items: [
      {
        to: "/admin/partner-payables",
        label: "Partner payables",
        icon: IconPayables,
        blurb: "Manage partner payments",
      },
      {
        to: "/escrow/refunds",
        label: "Refunds",
        icon: IconRefunds,
        blurb: "Review refund requests",
      },
      {
        to: "/admin/affiliate/partners",
        label: "Partner applications",
        icon: IconPartners,
        blurb: "Manage applications",
      },
      {
        to: "/admin/affiliate/redemptions",
        label: "Credit redemptions",
        icon: IconCredits,
        blurb: "Apply or reject credits",
      },
    ],
  },
  {
    heading: "System",
    icon: IconSystem,
    items: [
      {
        to: "/settlement-control",
        label: "Health",
        icon: IconHealth,
        blurb: "Check Gao services",
      },
      {
        to: "/devices",
        label: "Devices",
        icon: IconDevices,
        blurb: "Manage trusted devices",
      },
    ],
  },
  {
    heading: "Account",
    icon: IconSession,
    items: [
      {
        to: "/session",
        label: "Session",
        icon: IconSession,
        blurb: "View current session",
      },
      {
        to: "/login",
        label: "Login",
        icon: IconSession,
        blurb: "Sign in with owner wallet",
      },
    ],
  },
  {
    // ONE legacy entry. These five routes used to sit in the primary
    // navigation with individual "legacy" pills, which is five reminders per
    // page load of tools an operator is meant to reach for last.
    heading: "Legacy",
    icon: IconLegacy,
    legacy: true,
    items: [
      {
        to: "/money",
        label: "Money operations (old overview)",
        legacy: true,
        flags: ["legacy", "read-only"],
        icon: IconLegacy,
        blurb: "Superseded overview",
      },
      {
        to: "/admin/affiliate/withdrawals",
        label: "Legacy affiliate bucket payouts",
        legacy: true,
        flags: ["legacy", "deprecated"],
        icon: IconLegacy,
        blurb: "Pre-ledger payout queue",
      },
      {
        to: "/escrow/withdraw-treasury",
        label: "Legacy escrow treasury withdraw",
        legacy: true,
        flags: ["legacy", "deprecated"],
        icon: IconLegacy,
        blurb: "On-chain treasury withdrawal",
      },
      {
        to: "/escrow/withdraw-affiliate",
        label: "Legacy affiliate bucket sweep",
        legacy: true,
        flags: ["legacy", "deprecated"],
        icon: IconLegacy,
        blurb: "On-chain bucket sweep",
      },
      {
        to: "/escrow/buckets",
        label: "Legacy escrow buckets",
        legacy: true,
        flags: ["legacy", "read-only"],
        icon: IconLegacy,
        blurb: "Raw bucket balances",
      },
    ],
  },
];

/** Every destination, flattened. A section that is itself a destination
 *  contributes one item carrying its own heading as the label. */
export const NAV_ITEMS: ReadonlyArray<NavItem> = NAV_SECTIONS.flatMap((s) =>
  s.items !== undefined
    ? s.items
    : s.to !== undefined
      ? [
          {
            to: s.to,
            label: s.heading,
            icon: s.icon,
            blurb: s.blurb,
            legacy: s.legacy,
          },
        ]
      : [],
);

export function navItem(route: string): NavItem | undefined {
  return NAV_ITEMS.find((i) => i.to === route);
}

/** True when a section owns the given pathname, so the sidebar can open the
 *  group the operator is currently inside. Prefix-matched because detail
 *  routes (`/control-plane/gao-browser/edit`) belong to their list route. */
export function sectionOwnsPath(section: NavSection, pathname: string): boolean {
  const routes = section.items?.map((i) => i.to) ?? (section.to ? [section.to] : []);
  return routes.some((r) => pathname === r || pathname.startsWith(`${r}/`));
}
